/**
 * Town Ambassador one-pager (Iter 154).
 *
 * Printable, shareable poster a Community Partner can print and pin inside
 * cafés, community centres, town halls. Renders:
 *   - Partner / business name
 *   - Unique referral code
 *   - QR code that deep-links to the business signup with ?ref=CODE attached
 *   - Headline + the three trust signals
 *   - Simple call to action
 *
 * URL: /partners/poster?ref=F2D-000123
 *      Optional: ?name=Maradels%20Holistic to override the displayed name.
 *
 * No auth required — anyone with a partner code can render their own poster.
 * The page validates the code first so a bad URL doesn't print a useless poster.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import axios from 'axios';
import { Printer, ArrowLeft, AlertTriangle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TownAmbassadorPoster = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const code = (params.get('ref') || '').trim();
  const overrideName = (params.get('name') || '').trim();
  const canvasRef = useRef(null);

  const [validating, setValidating] = useState(true);
  const [valid, setValid] = useState(false);
  const [partnerName, setPartnerName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!code) {
      setError('Missing referral code. Add ?ref=YOUR-CODE to the URL.');
      setValidating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/referral/validate/${encodeURIComponent(code)}`);
        if (cancelled) return;
        if (r.data?.valid) {
          setValid(true);
          setPartnerName(overrideName || r.data.helperName || code);
        } else {
          setError('This referral code is not active. Please double-check it.');
        }
      } catch {
        if (!cancelled) setError('Could not validate the referral code. Try again in a moment.');
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [code, overrideName]);

  // Render the QR code (signup deep-link with ?ref=CODE)
  useEffect(() => {
    if (!valid || !canvasRef.current) return;
    const url = `${window.location.origin}/business/signup?ref=${encodeURIComponent(code)}`;
    QRCode.toCanvas(canvasRef.current, url, {
      width: 360,
      margin: 1,
      errorCorrectionLevel: 'H',
      color: { dark: '#3b0764', light: '#ffffff' },
    }).catch(() => { /* noop */ });
  }, [valid, code]);

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-6 w-6 text-purple-600 animate-spin" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md text-center bg-white rounded-2xl shadow border border-gray-200 p-8" data-testid="poster-invalid">
          <AlertTriangle className="h-10 w-10 text-amber-600 mx-auto mb-3" />
          <h1 className="text-xl font-semibold text-gray-900">Can't print this poster</h1>
          <p className="text-sm text-gray-600 mt-2">{error}</p>
          <button
            onClick={() => navigate('/affiliate')}
            className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Community Partners
          </button>
        </div>
      </div>
    );
  }

  const signupUrl = `${window.location.origin}/business/signup?ref=${encodeURIComponent(code)}`;

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Print toolbar — hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between" data-testid="poster-toolbar">
        <button
          onClick={() => navigate('/affiliate')}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-purple-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 active:bg-purple-800"
          data-testid="poster-print-btn"
        >
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      {/* The actual A4-friendly poster */}
      <div
        className="mx-auto my-6 print:my-0 bg-white shadow-xl print:shadow-none border border-gray-200 print:border-0"
        style={{ width: '210mm', minHeight: '297mm' }}
        data-testid="poster-sheet"
      >
        <div className="h-full p-10 flex flex-col">
          {/* Brand strip */}
          <div className="text-purple-700 text-sm font-semibold tracking-[0.25em] uppercase">
            Find-2-Day · Community Partner
          </div>

          {/* Headline */}
          <h1 className="mt-6 text-5xl font-bold text-gray-900 leading-tight">
            Help your town<br/>stay connected.
          </h1>
          <p className="mt-4 text-xl text-gray-700 leading-snug max-w-prose">
            Discover local events, offers and updates — and support the businesses
            in your community.
          </p>

          {/* Trust pillars */}
          <div className="mt-8 grid grid-cols-3 gap-4" data-testid="poster-pillars">
            <Pillar title="60-day free trial" body="Paid plans free for 60 days; Free Directory always free." />
            <Pillar title="No email needed" body="Browse and follow events the moment you open the app." />
            <Pillar title="Built with locals" body="Owned by your neighbours, not a global platform." />
          </div>

          {/* QR + CTA panel */}
          <div className="mt-10 rounded-2xl bg-purple-50 border-2 border-purple-200 p-6 flex items-center gap-6" data-testid="poster-qr-panel">
            <div className="bg-white p-3 rounded-xl border border-purple-200 shadow-sm">
              <canvas ref={canvasRef} className="block" data-testid="poster-qr-canvas" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold text-purple-900 leading-tight">
                Scan to list your business free
              </p>
              <p className="mt-2 text-base text-purple-900/80 leading-snug">
                Sign up with my Community Partner code and you get a permanent
                local advocate looking out for your listing.
              </p>
              <div className="mt-4">
                <p className="text-xs uppercase tracking-widest text-purple-700/70 font-semibold">My partner code</p>
                <p className="mt-1 font-mono text-3xl font-bold text-purple-900" data-testid="poster-code">{code}</p>
                <p className="mt-2 text-xs text-purple-700/70 break-all">{signupUrl}</p>
              </div>
            </div>
          </div>

          {/* Earnings line */}
          <p className="mt-6 text-sm text-gray-600 italic">
            I earn 50% from everything the businesses I introduce pay — subscriptions, renewals, boosts and any future paid product — a small thank-you for helping our town stay connected.
          </p>

          {/* Footer */}
          <div className="mt-auto pt-8 flex items-end justify-between border-t border-gray-100">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">Hosted by</p>
              <p className="text-base font-semibold text-gray-800 mt-1" data-testid="poster-partner-name">{partnerName}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">find-2-day.com</p>
              <p className="text-base text-gray-500 mt-1">Discover. Connect. Belong.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print rules: hide everything else and force the poster to fill the page. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
};

const Pillar = ({ title, body }) => (
  <div className="border-l-2 border-purple-300 pl-3">
    <p className="text-sm font-bold text-purple-900">{title}</p>
    <p className="text-xs text-gray-600 mt-1 leading-snug">{body}</p>
  </div>
);

export default TownAmbassadorPoster;
